import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse, stringify } from 'qs'
import { Box, Tooltip } from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'

import { TableUrlPagination } from '../../../../ui'
import { SIR_TABLE_COLUMNS, ESTATE_CLOSED_VALUE, ESTATE_CLOSED_COLOR } from './sir-table.constants'
import { DEFAULT_QUERY } from '../../reducers'
import { useSirContext } from '../../contexts'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const SirTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useSelector((state) => state.combos)
  const {
    data,
    pagination: { total_solicitudes: totalEntries },
    loading,
    search,
    onClickTableRow,
    setSelectedTableRows,
    getTableData,
  } = useSirContext()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getTableData(searchParams)
    } else {
      navigate(`/sir?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      SIR_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.sir.table.columns.${column.field}.title` }),
          size: column.width,
        }

        if (column.field === 'comunicaciones_pendientes') {
          return {
            ...baseColumn,
            header: '',
            Header: () => (
              <Tooltip
                title={formatMessage({ id: `pages.sir.table.columns.${column.field}.title` })}
              >
                <NotificationsNoneIcon sx={{ fontSize: 18 }} />
              </Tooltip>
            ),
            Cell: ({ row }) =>
              row.original.comunicaciones_pendientes > 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center">
                  <NotificationsActiveIcon color="secondary" sx={{ fontSize: 18 }} />
                </Box>
              ) : null,
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return <span>{lookup[value] ?? value ?? '--'}</span>
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
        }
      }),
    [combos, formatMessage]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      draggable: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      onClickTableRow(row.id)
    },
    [onClickTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={data}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
      muiTableBodyRowProps={({ row }) => {
        const bgColor =
          row.original['estado_finca'] === ESTATE_CLOSED_VALUE ? ESTATE_CLOSED_COLOR : 'inherit'
        return {
          sx: {
            backgroundColor: bgColor,
            '& td': {
              backgroundColor: bgColor,
            },
          },
        }
      }}
    />
  )
}
